import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useMemo,
} from "react";
import { Table, Button, Form, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import {
  PencilSquare,
  KeyFill,
  ArrowRepeat,
  Search,
  FileEarmarkExcel,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";

import ModalCuentaAdmin from "./modaladmin/ModalCuentaAdmin";
import ModalChangePassword from "./modaladmin/ModalChangePassword";

import {
  insertAdmin,
  updateAdmin,
  getAllAdmin,
  UpdateEstadoAdmin,
  changePasswordAdmin,
  getAdminCredentials,
} from "../../../Api/AdminApi";

import { getChequesByFechaCreacion } from "../../../Api/ChequesApi";

/* ================= REDUCER MODAL ================= */
const showReducer = (cur, action) => {
  switch (action.type) {
    case "SHOW":
      return {
        isShow: true,
        typeModal: action.typeModal,
        item: action.item || null,
      };
    case "END":
      return { ...cur, isShow: false };
    default:
      return cur;
  }
};

/* ================= CONSTANTES ================= */
const ADMIN_PAGE_SIZE = 10;
const CHEQUE_PAGE_SIZE = 20;

/* ================= HELPERS ================= */
const paginate = (array, page, size) => {
  const start = (page - 1) * size;
  return array.slice(start, start + size);
};

const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

const fmtDate = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  return s.split("T")[0];
};

const fmtDateTime = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  const [date, time] = s.split("T");
  return `${date} ${time?.slice(0, 5) || ""}`.trim();
};

const fmtPY = (n) => Number(n || 0).toLocaleString("es-PY");

const badgeEstado = (estado) => {
  const e = (estado || "").toString().toUpperCase();
  if (e === "ANULADO" || e === "INACTIVO") return "danger";
  if (e === "ENTREGADO") return "success";
  if (e === "IMPRESO") return "primary";
  if (e === "CREADO") return "secondary";
  return "dark";
};

const PaginationBar = ({ page, totalPages, onPrev, onNext }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page <= 1}
        onClick={onPrev}
        title="Anterior"
      >
        <ChevronLeft />
      </Button>
      <span className="small">
        Página <b>{page}</b> / {totalPages}
      </span>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={page >= totalPages}
        onClick={onNext}
        title="Siguiente"
      >
        <ChevronRight />
      </Button>
    </div>
  );
};

/* ================= COMPONENT ================= */
export const Admin = () => {
  /* ================= ESTADOS ================= */
  const [admins, setAdmins] = useState([]);
  const [userdata, setUserdata] = useState(null);

  const [datoAdmin, setDatoAdmin] = useState({});
  const [isNewAdmin, setIsNewAdmin] = useState(true);

  // Cheques
  const [cheques, setCheques] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [loadingCheques, setLoadingCheques] = useState(false);

  // Filtros cheques
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  // Paginación
  const [adminPage, setAdminPage] = useState(1);
  const [chequePage, setChequePage] = useState(1);

  const [modal, dispatchModal] = useReducer(showReducer, {
    isShow: false,
    typeModal: null,
    item: null,
  });

  /* ================= LOAD ================= */
  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const [adminResult, userData] = await Promise.all([
        getAllAdmin(),
        getAdminCredentials(),
      ]);
      setAdmins(adminResult);
      setUserdata(userData);
      setAdminPage(1);
    } catch {
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    }
  }, []);

  /* ================= ADMIN ================= */
  const handleNewAdmin = () => {
    setDatoAdmin({});
    setIsNewAdmin(true);
    dispatchModal({ type: "SHOW", typeModal: "ADMIN" });
  };

  const handleEditAdmin = (item) => {
    setDatoAdmin(item);
    setIsNewAdmin(false);
    dispatchModal({ type: "SHOW", typeModal: "ADMIN" });
  };

  const handleChangePassword = (item) => {
    dispatchModal({
      type: "SHOW",
      typeModal: "CHANGE_PASSWORD",
      item: { idadmin: item.idadmin, cuenta: item.cuenta },
    });
  };

  const onReturnDataAdmin = async (data, isnew, typeModal) => {

    dispatchModal({ type: "END" });
    Swal.fire({ title: "Procesando...", allowOutsideClick: false });

    try {
      if (typeModal === "CHANGE_PASSWORD") {
        await changePasswordAdmin(data);
      } else {
        isnew ? await insertAdmin(data) : await updateAdmin(data);
        await loadAdmins();
      }
      Swal.fire("Éxito", "Operación realizada", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Ocurrió un error", "error");
    }
  };

  const changeEstado = async (id) => {
    const r = await Swal.fire({
      title: "¿Cambiar estado?",
      icon: "warning",
      showCancelButton: true,
    });
    if (r.isConfirmed) {
      await UpdateEstadoAdmin(id);
      await loadAdmins();
    }
  };

  /* ================= CHEQUES ================= */
  const buscarCheques = async () => {
    if (!desde || !hasta) {
      Swal.fire("Atención", "Debe indicar fechas", "warning");
      return;
    }

    try {
      setLoadingCheques(true);
      const data = await getChequesByFechaCreacion(desde, hasta);
      setCheques(Array.isArray(data) ? data : []);
      setChequePage(1);
    } catch (err) {
      setCheques([]);
      Swal.fire("Sin resultados", err.message, "error");
    } finally {
      setLoadingCheques(false);
    }
  };

  /* ================= FILTRO CHEQUES ================= */
  const chequesFiltrados = useMemo(() => {
    const q = normalize(filtroTexto);

    return cheques.filter((c) => {
      const coincideTexto = q
        ? [
            c.idcheque,
            c.chequenumero,
            c.serie,
            c.nombre,
            c.orden,
            c.concepto,
            c.deposito,
            c.numeroderecibo,
            c.usuariocreacion,
            c.usuarioedicion,
            c.motivodeanulado,
          ]
            .map(normalize)
            .some((x) => x.includes(q))
        : true;

      const coincideEstado = filtroEstado
        ? (c.estado || "").toString().toUpperCase() === filtroEstado
        : true;

      return coincideTexto && coincideEstado;
    });
  }, [cheques, filtroTexto, filtroEstado]);

  useEffect(() => {
    setChequePage(1);
  }, [filtroTexto, filtroEstado, desde, hasta]);

  /* ================= PAGINACIÓN ================= */
  const totalAdminPages = Math.ceil(admins.length / ADMIN_PAGE_SIZE) || 1;
  const totalChequePages =
    Math.ceil(chequesFiltrados.length / CHEQUE_PAGE_SIZE) || 1;

  const adminsPaginados = useMemo(
    () => paginate(admins, adminPage, ADMIN_PAGE_SIZE),
    [admins, adminPage]
  );

  const chequesPaginados = useMemo(
    () => paginate(chequesFiltrados, chequePage, CHEQUE_PAGE_SIZE),
    [chequesFiltrados, chequePage]
  );

  /* ================= EXCEL (TODOS LOS CAMPOS) ================= */
  const descargarExcel = () => {
    if (chequesFiltrados.length === 0) return;

    const data = chequesFiltrados.map((c) => ({
      idcheque: c.idcheque,
      chequenumero: c.chequenumero,
      serie: c.serie,
      monto: c.monto,
      fechaemision: fmtDate(c.fechaemision),
      nombre: c.nombre,
      orden: c.orden,
      concepto: c.concepto,
      saldoanterior: c.saldoanterior,
      deposito: c.deposito,
      total: c.total,
      estecheque: c.estecheque,
      saldo: c.saldo,
      idordendepago: c.idordendepago,
      idliquidacion: c.idliquidacion,
      estado: c.estado,
      obs: c.obs,
      usuariocreacion: c.usuariocreacion,
      fechacreacion: fmtDateTime(c.fechacreacion),
      usuarioedicion: c.usuarioedicion,
      fechaedicion: fmtDateTime(c.fechaedicion),
      numeroderecibo: c.numeroderecibo,
      motivodeanulado: c.motivodeanulado,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cheques");
    XLSX.writeFile(wb, `cheques_${desde}_${hasta}.xlsx`);
  };

  /* ================= RENDER ================= */
  return (
    <div className="row">
      {/* ================= ADMIN ================= */}
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Cuentas Administrativas</h4>
            <Button size="sm" onClick={handleNewAdmin}>
              <PencilSquare className="me-1" /> Agregar
            </Button>
          </div>

          <div className="card-body">
            <Table responsive  size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>#</th>
                  <th style={{ width: 180 }}>Cuenta</th>
                  <th>Nombre</th>
                  <th style={{ width: 120 }}>Estado</th>
                  <th style={{ width: 170 }}>Opciones</th>
                </tr>
              </thead>
              <tbody>
                {adminsPaginados.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      Sin datos
                    </td>
                  </tr>
                )}

                {adminsPaginados.map((a) => (
                  <tr key={a.idadmin}>
                    <td>{a.idadmin}</td>
                    <td>{a.cuenta}</td>
                    <td>{a.nombrecompleto}</td>
                    <td>
                      <Badge bg={a.estado === "INACTIVO" ? "danger" : "success"}>
                        {a.estado}
                      </Badge>
                    </td>
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEditAdmin(a)}
                        title="Editar"
                      >
                        <PencilSquare />
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleChangePassword(a)}
                        title="Cambiar contraseña"
                      >
                        <KeyFill />
                      </Button>{" "}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => changeEstado(a.idadmin)}
                        title="Cambiar estado"
                      >
                        <ArrowRepeat />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <PaginationBar
              page={adminPage}
              totalPages={totalAdminPages}
              onPrev={() => setAdminPage((p) => p - 1)}
              onNext={() => setAdminPage((p) => p + 1)}
            />
          </div>
        </div>
      </div>

      {/* ================= CHEQUES ================= */}
      <div className="col-xl-12 mt-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Cheques por Fecha</h4>
            <Button
              variant="success"
              size="sm"
              onClick={descargarExcel}
              disabled={chequesFiltrados.length === 0}
              title="Exporta TODOS los campos del cheque"
            >
              <FileEarmarkExcel className="me-1" /> Excel
            </Button>
          </div>

          <div className="card-body">
            {/* Filtros */}
            <div className="row mb-3 g-2">
              <div className="col-md-2">
                <Form.Control
                  type="date"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <Form.Control
                  type="date"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </div>
              <div className="col-md-2 d-grid">
                <Button onClick={buscarCheques} disabled={loadingCheques}>
                  <Search className="me-1" />
                  {loadingCheques ? "Buscando..." : "Buscar"}
                </Button>
              </div>
              <div className="col-md-3">
                <Form.Control
                  placeholder="Buscar (cheque, nombre, orden, concepto...)"
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <Form.Select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <option value="">Todos los estados</option>
                  <option value="CREADO">CREADO</option>
                  <option value="IMPRESO">IMPRESO</option>
                  <option value="ENTREGADO">ENTREGADO</option>
                  <option value="ANULADO">ANULADO</option>
                </Form.Select>
              </div>
            </div>

            <div className="d-flex justify-content-between mb-2">
              <small className="text-muted">
                Total: <b>{chequesFiltrados.length}</b>
              </small>
              <small className="text-muted">
                Página: <b>{chequePage}</b> / {totalChequePages}
              </small>
            </div>

            {/* Tabla cheques (compacta) */}
            <Table responsive  size="sm" className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>ID</th>
                  <th style={{ width: 120 }}>Cheque</th>
                  <th style={{ width: 110 }}>Serie</th>
                  <th>Beneficiario</th>
                  <th className="text-end" style={{ width: 140 }}>
                    Monto
                  </th>
                  <th style={{ width: 110 }}>Emisión</th>
                  <th style={{ width: 160 }}>Creación</th>
                  <th style={{ width: 120 }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {chequesPaginados.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      Sin datos
                    </td>
                  </tr>
                )}

                {chequesPaginados.map((c) => (
                  <tr key={c.idcheque}>
                    <td>{c.idcheque}</td>
                    <td>{c.chequenumero}</td>
                    <td>{c.serie}</td>
                    <td title={c.nombre}>{c.nombre}</td>
                    <td className="text-end">{fmtPY(c.monto)}</td>
                    <td>{fmtDate(c.fechaemision)}</td>
                    <td title={c.usuariocreacion || ""}>
                      {fmtDateTime(c.fechacreacion)}
                    </td>
                    <td>
                      <Badge bg={badgeEstado(c.estado)}>{c.estado}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <PaginationBar
              page={chequePage}
              totalPages={totalChequePages}
              onPrev={() => setChequePage((p) => p - 1)}
              onNext={() => setChequePage((p) => p + 1)}
            />
          </div>
        </div>
      </div>

      {/* ================= MODALS ================= */}
      {modal.isShow && modal.typeModal === "ADMIN" && (
        <ModalCuentaAdmin
          estado
          item={datoAdmin}
          isnew={isNewAdmin}
          onCloseModal={() => dispatchModal({ type: "END" })}
          onReturnValue={onReturnDataAdmin}
        />
      )}

      {modal.isShow && modal.typeModal === "CHANGE_PASSWORD" && (
        <ModalChangePassword
          estado
          item={modal.item}
          typeModal={modal.typeModal}
          onCloseModal={() => dispatchModal({ type: "END" })}
          onReturnValue={onReturnDataAdmin}
        />
      )}
    </div>
  );
};

export default Admin;
